import { useNavigate } from "react-router-dom";

import camelcaseKeys from "camelcase-keys";
import { AxiosError, AxiosResponse } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AXIOS } from "hooks/axios";
import { useAuth } from "components/auth/AuthProvider";
import { useError } from "components/status/ErrorProvider";
import { useSuccess } from "components/status/SuccessProvider";
import { ServiceSchema } from "./services";
import { MetaSchema, PaginationSchema } from "hooks/pagination";

export type HostBase = { data: string | number | null; error: string | null };

export type Snapshot = {
  meta: {
    os: string;
    version: string;
    timezone: string;
    hardware: "virtual" | "physical";
    inDocker: boolean;
  };
  base: {
    ip: HostBase;
    fan: HostBase;
    role: HostBase;
    clock: HostBase;
    kernel: HostBase;
    productName: HostBase;
    serialNumber: HostBase;
    powerSupply1: HostBase;
    powerSupply2: HostBase;
    systemVersion: HostBase;
    portalVersion?: HostBase;
  };
  resources: {
    cpuUsage: HostBase;
    cpuTemperature: HostBase;
    memory: {
      data: {
        total: number;
        free: number;
        available: number;
      };
      error?: string;
    };
    diskLogical: {
      data: {
        core: LogicalDiskInfoSchemaBase[];
        raw: LogicalDiskInfoSchemaBase[];
      };
      error?: string;
    };
    diskPhysical: {
      data: {
        core: PhysicalDiskInfoSchemaBase[];
        raw: string;
      };
      error?: string;
    };
  };
  hsm: {
    data: {
      core: HSMCoreSchema;
      raw: any;
    };
    error?: string;
  };
  logs: {
    name: string;
    data: string[];
    error: string | null;
  }[];
  services: ServiceSchema[];
};

export type ProjectConfigSchema = {
  id: string;
  ip: string;
  host: string;
  version: string;
  location: string;
  description: string;
  scommBaseUri: string;
  sstatBaseUri: string;
  serialNumber: string;
  hardware: "virtual" | "physical";
  history: { on: boolean; interval: number };
  latest: {
    updatedAt: string | Date;
    data: Snapshot;
  };
};

export type ProjectSchema = {
  id: string;
  uri: string;
  tags: string[];
  name: string;
  status: string;
  online: boolean;
  description: string;
  infrastructure: "on-prem" | "hosted" | "mixed";
  portalHost?: string;
  portalVersion?: string;
  portalVersionUpdatedAt?: string;
  user: {
    email: string;
    connected: boolean;
    lastActive: Date;
  };
};

export const usePavmAppliances = (body?: PaginationSchema) => {
  const { user } = useAuth();
  const uri = user?.project?.uri;

  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    { data: ProjectConfigSchema[]; meta: MetaSchema }
  >(
    [`PAVM APPLIANCES ${uri} BODY ${JSON.stringify(body)}`],
    () => AXIOS.post<ProjectConfigSchema[]>(`${uri}/appliances`, body || {}),
    {
      enabled: !!uri && !!body,
      staleTime: 1000 * 120,
      select: (data) => camelcaseKeys(data.data, { deep: true }),
    }
  );
};

type ConfigUpdateSchema = {
  uri: string;
  config: {
    ip: string;
    host: string;
    location: string;
    scommBaseUri: string;
    sstatBaseUri: string;
    history: {
      on: boolean;
      interval: number;
    };
  };
};

export const usePavmApplianceEditConfig = () => {
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const appliances = usePavmAppliances();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    ConfigUpdateSchema
  >(
    [`PAVM APPLIANCE EDIT CONFIG`],
    ({ uri, config }: ConfigUpdateSchema) =>
      AXIOS.post(`${uri}/appliances/edit/config`, config),

    {
      onSuccess: (data) => {
        setSuccess("Config updated");

        // refetch appliances to show latest changes
        appliances.refetch();

        return camelcaseKeys(data.data.config, { deep: true });
      },
      onError: (err) => {
        const message = err?.response?.data?.message ?? "";
        setError({ message, reset: mutation.reset });
      },
    }
  );

  return mutation;
};

type usePavmApplianceEditDescriptionSchema = {
  id: string;
  description: string;
};
export const usePavmApplianceEditDescription = () => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    usePavmApplianceEditDescriptionSchema
  >(
    [`PAVM APPLIANCE EDIT DESCRIPTION`],
    (props: usePavmApplianceEditDescriptionSchema) =>
      AXIOS.post(`${auth.user.project.uri}/appliances/edit/description`, props),

    {
      onSuccess: (data) => {
        setSuccess("Description updated");

        // refetch appliances to show latest changes
        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("PAVM APPLIANCES"),
        });

        return camelcaseKeys(data.data.config, { deep: true });
      },
      onError: (err) => {
        const message = err?.response?.data?.message ?? "";
        setError({ message, reset: mutation.reset });
      },
    }
  );

  return mutation;
};

type ConnectSchema = {
  uri: string | undefined;
  projectId: string | undefined;
};

export const usePavmConnect = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    ConnectSchema
  >(
    [`PAVM CONNECT`],
    ({ uri, projectId }) => AXIOS.post(`${uri}/connect`, { projectId }),
    {
      onSuccess: (data) => {
        const { project, user } = camelcaseKeys(data.data, { deep: true });

        setUser(user);
        navigate(`/projects/${project.name?.toLowerCase()}`);

        setSuccess("Connected");
      },
      onError: (err) => {
        const message = err?.response?.data?.message ?? "";
        setError({ message, reset: mutation.reset });
      },
    }
  );

  return mutation;
};

export const usePavmDisconnect = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    ConnectSchema & { email: string }
  >(
    [`PAVM DISCONNECT`],
    ({ uri, email, projectId }) =>
      AXIOS.post(`${uri}/disconnect`, { email, projectId }),
    {
      onSuccess: (data) => {
        setSuccess("Disconnected");

        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("USER SERVICE USERS"),
        });
        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("USER SERVICE PROJECTS"),
        });

        return camelcaseKeys(data.data.user, { deep: true });
      },
      onError: (err) => {
        const message = err?.response?.data?.message ?? "";
        setError({ message, reset: mutation.reset });
      },
    }
  );

  return mutation;
};

export type HSMCoreSchema = {
  os: string;
  model: string;
  state: string;
  module: string;
  vendor: string;
  temperature: string;
  adapterClock: string;
  serialNumber: string;
  firmwareVersion: string;
};

export type LogicalDiskInfoSchemaBase = {
  filesystem: string;
  size: string;
  used: string;
  avail: string;
  use: string;
  mountedOn: string;
};

export type DiskInfoSchema = {
  core: LogicalDiskInfoSchemaBase[];
  raw: LogicalDiskInfoSchemaBase[];
};

export type PhysicalDiskInfoSchemaBase = {
  model: string;
  serialNumber: string;
  firmware: string;
  totalSize: string;
  powerOnHours: string;
  powerCycleCount: string;
};

export type MemorySchema = {
  available: string;
  free: string;
  total: string;
};

export type HostLiveSchema = { updatedAt: Date; data: Snapshot };

export const usePavmLive = (host: ProjectConfigSchema["host"]) => {
  const { user } = useAuth();
  const uri = user?.project?.uri;

  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    HostLiveSchema
  >(
    [`PAVM HOST LIVE ${uri} ${host}`],
    () => AXIOS.post<HostLiveSchema>(`${uri}/live`, { host }),
    {
      retry: false,
      staleTime: 1000 * 120,
      refetchOnMount: false,
      enabled: !!uri,
      select: (data) => camelcaseKeys(data.data as any, { deep: true }),
    }
  );
};

export const usePavmLatest = (host: string) => {
  const { user } = useAuth();
  const uri = user?.project?.uri;

  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    Snapshot
  >(
    [`PAVM HOST LATEST ${uri} ${host}`],
    () => AXIOS.post(`${uri}/latest`, { host }),
    {
      retry: 2,
      staleTime: 1000 * 120,
      refetchOnMount: false,
      enabled: !!uri,
      select: (data) => camelcaseKeys(data.data.data as any, { deep: true }),
    }
  );
};
